/**
 * Navbar Component
 * Shared navigation bar for all pages (except create/edit pages)
 * Includes: Logo, main nav, Create dropdown, auth links
 * Mobile-responsive with hamburger menu
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../styles/colors.css';

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAuthenticated } = useAuth();
  const [showCreateDropdown, setShowCreateDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const dropdownTimeoutRef = React.useRef(null);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Improved dropdown handlers with delay
  const handleMouseEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setShowCreateDropdown(true);
  };

  const handleMouseLeave = () => {
    // Add small delay before closing
    dropdownTimeoutRef.current = setTimeout(() => {
      setShowCreateDropdown(false);
    }, 200); // 200ms delay
  };

  // Click item handler
  const handleDropdownClick = (path) => {
    setShowCreateDropdown(false);
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    navigate(path);
  };

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current);
      }
    };
  }, []);

  // Check if user can create events (only bands, venues, promoters)
  const canCreateEvents = user && ['band', 'venue', 'promoter'].includes(user.account_role);
  
  // Check if user can create opportunities (all except fans) - UPDATED for Batch 2
  const canCreateOpportunities = isAuthenticated; // All users can now create opportunities

  return (
    <>
      <style>{`
        .navbar-container {
          background-color: var(--coal-black);
          color: var(--text-light);
          display: flex;
          flex-direction: column;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          width: 100%;
          z-index: 1000;
          border-bottom: 2px solid var(--accent-primary);
        }

        .nav-top-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 24px 40px;
          position: relative;
          height: auto;
        }

        .nav-bottom-row {
          background-color: rgba(74, 74, 74, 0.3);
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 18px 40px;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          position: relative;
          height: auto;
        }

        .subterra-logo {
          font-size: 3rem;
          font-family: serif;
          cursor: pointer;
          letter-spacing: 3px;
          font-weight: 200;
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
          transition: all 0.3s ease;
        }

        .hamburger-btn {
          display: none;
        }

        .mobile-menu-overlay {
          display: none;
        }

        .desktop-auth-section {
          display: flex;
          gap: 28px;
          align-items: center;
          min-width: 140px;
          justify-content: flex-end;
        }

        /* Mobile styles */
        @media (max-width: 768px) {
          .nav-top-row {
            height: 75px;
            padding: 0 16px;
          }

          .nav-bottom-row {
            height: 50px;
            padding: 0 16px;
          }

          .subterra-logo {
            font-size: 2rem;
            position: fixed !important;
            left: 50% !important;
            transform: translateX(-50%) !important;
            letter-spacing: 2px;
          }

          .desktop-auth-section {
            display: none !important;
          }

          .hamburger-btn {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            background: none;
            border: none;
            cursor: pointer;
            padding: 8px;
            width: 40px;
            height: 40px;
            position: relative;
            z-index: 1001;
          }

          .hamburger-line {
            width: 28px;
            height: 3px;
            background: var(--accent-primary);
            margin: 3px 0;
            transition: all 0.3s ease;
            border-radius: 2px;
          }

          .hamburger-btn.open .hamburger-line:nth-child(1) {
            transform: rotate(45deg) translate(7px, 7px);
          }

          .hamburger-btn.open .hamburger-line:nth-child(2) {
            opacity: 0;
          }

          .hamburger-btn.open .hamburger-line:nth-child(3) {
            transform: rotate(-45deg) translate(7px, -7px);
          }

          .create-dropdown-mobile {
            position: absolute;
            top: 100%;
            left: 0;
            margin-top: 4px;
            background-color: var(--coal-black);
            border: 1px solid var(--border-dark);
            border-radius: 4px;
            min-width: 180px;
            z-index: 1000;
            box-shadow: 0 4px 12px rgba(0,0,0,0.4);
          }

          .create-dropdown-mobile button {
            font-size: 16px !important;
            padding: 14px 16px !important;
          }

          .mobile-menu-overlay {
            display: block;
            position: fixed;
            top: 125px;
            right: 0;
            background: var(--coal-black);
            border-left: 2px solid var(--accent-primary);
            border-bottom: 2px solid var(--accent-primary);
            min-width: 220px;
            box-shadow: -4px 4px 16px rgba(0, 0, 0, 0.6);
            z-index: 999;
            transform: translateX(100%);
            transition: transform 0.3s ease;
          }

          .mobile-menu-overlay.open {
            transform: translateX(0);
          }

          .mobile-menu-item {
            display: block;
            width: 100%;
            padding: 18px 24px;
            background: none;
            border: none;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
            color: var(--text-light);
            cursor: pointer;
            font-size: 16px;
            font-weight: 500;
            text-align: left;
            transition: all 0.2s ease;
          }

          .mobile-menu-item:hover {
            background: var(--smoke-gray);
            color: var(--accent-primary);
          }

          .mobile-menu-item:last-child {
            border-bottom: none;
          }

          .nav-bottom-row {
            justify-content: center;
            gap: 0;
          }

          .nav-link {
            position: static !important;
            transform: none !important;
            font-size: 18px !important;
            letter-spacing: 0.8px !important;
            margin: 0 16px !important;
          }
        }
      `}</style>

      <div className="navbar-container">
        {/* Top row: Create + Subterra + Hamburger/Auth */}
        <div className="nav-top-row">
          {/* Left: Create dropdown */}
          <div
            style={{ position: 'relative', minWidth: '140px' }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {isAuthenticated ? (
              <>
                <button
                  style={{
                    background: 'transparent',
                    border: '2px solid var(--accent-primary)',
                    color: 'var(--accent-primary)',
                    cursor: 'pointer',
                    fontSize: '16px',
                    padding: '10px 20px',
                    borderRadius: '6px',
                    fontWeight: '600',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={() => setShowCreateDropdown(!showCreateDropdown)}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'var(--accent-primary)';
                    e.target.style.color = 'var(--coal-black)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'transparent';
                    e.target.style.color = 'var(--accent-primary)';
                  }}
                >
                  Create ▼
                </button>
                
                {/* Dropdown menu */}
                {showCreateDropdown && (
                  <div className="create-dropdown-mobile" style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    marginTop: '4px',
                    backgroundColor: 'var(--coal-black)',
                    border: '1px solid var(--border-dark)',
                    borderRadius: '4px',
                    minWidth: '180px',
                    zIndex: 1000,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.4)'
                  }}>
                    {canCreateEvents && (
                      <button
                        onClick={() => handleDropdownClick('/events/create')}
                        style={{
                          display: 'block',
                          width: '100%',
                          padding: '10px 16px',
                          background: 'none',
                          border: 'none',
                          color: 'var(--text-light)',
                          cursor: 'pointer',
                          fontSize: '14px',
                          textAlign: 'left',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = 'var(--smoke-gray)';
                          e.target.style.color = 'var(--accent-primary)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.color = 'var(--text-light)';
                        }}
                      >
                        Create Event
                      </button>
                    )}
                    
                    {canCreateOpportunities && (
                      <button
                        onClick={() => handleDropdownClick('/opportunities/create')}
                        style={{
                          display: 'block',
                          width: '100%',
                          padding: '10px 16px',
                          background: 'none',
                          border: 'none',
                          color: 'var(--text-light)',
                          cursor: 'pointer',
                          fontSize: '14px',
                          textAlign: 'left',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = 'var(--smoke-gray)';
                          e.target.style.color = 'var(--accent-primary)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.color = 'var(--text-light)';
                        }}
                      >
                        Create Opportunity
                      </button>
                    )}
                    
                    <button
                      onClick={() => handleDropdownClick('/articles/create')}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '10px 16px',
                        background: 'none',
                        border: 'none',
                        color: 'var(--text-light)',
                        cursor: 'pointer',
                        fontSize: '14px',
                        textAlign: 'left',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = 'var(--smoke-gray)';
                        e.target.style.color = 'var(--accent-primary)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = 'var(--text-light)';
                      }}
                    >
                      Create Article
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div style={{ width: '140px' }}></div>
            )}
          </div>

          {/* Center: Logo */}
          <div
            onClick={() => navigate('/')}
            className="subterra-logo"
            onMouseEnter={(e) => {
              e.target.style.color = 'var(--accent-primary)';
            }}
            onMouseLeave={(e) => {
              e.target.style.color = 'var(--text-light)';
            }}
          >
            Subterra
          </div>

          {/* Right: Desktop auth / Mobile hamburger */}
          <div className="desktop-auth-section">
            {isAuthenticated ? (
              <>
                <button
                  onClick={() => navigate('/profile')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-light)',
                    cursor: 'pointer',
                    fontSize: '15px',
                    fontWeight: '500',
                    padding: '8px 0',
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = 'var(--accent-primary)';
                    const underline = e.currentTarget.querySelector('.underline');
                    if (underline) underline.style.width = '100%';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = 'var(--text-light)';
                    const underline = e.currentTarget.querySelector('.underline');
                    if (underline) underline.style.width = '0';
                  }}
                >
                  My Account
                  <div className="underline" style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    height: '2px',
                    width: 0,
                    background: 'var(--accent-primary)',
                    transition: 'width 0.3s ease'
                  }}></div>
                </button>
                
                <button
                  onClick={() => navigate('/collaborations/requests')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-light)',
                    cursor: 'pointer',
                    fontSize: '15px',
                    fontWeight: '500',
                    padding: '8px 0',
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = 'var(--accent-primary)';
                    const underline = e.currentTarget.querySelector('.underline');
                    if (underline) underline.style.width = '100%';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = 'var(--text-light)';
                    const underline = e.currentTarget.querySelector('.underline');
                    if (underline) underline.style.width = '0';
                  }}
                >
                  Collab Requests
                  <div className="underline" style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    height: '2px',
                    width: 0,
                    background: 'var(--accent-primary)',
                    transition: 'width 0.3s ease'
                  }}></div>
                </button>

                <button
                  onClick={handleLogout}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-light)',
                    cursor: 'pointer',
                    fontSize: '15px',
                    fontWeight: '500',
                    padding: '8px 0',
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = 'var(--accent-primary)';
                    const underline = e.currentTarget.querySelector('.underline');
                    if (underline) underline.style.width = '100%';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = 'var(--text-light)';
                    const underline = e.currentTarget.querySelector('.underline');
                    if (underline) underline.style.width = '0';
                  }}
                >
                  Sign Out
                  <div className="underline" style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    height: '2px',
                    width: 0,
                    background: 'var(--accent-primary)',
                    transition: 'width 0.3s ease'
                  }}></div>
                </button>
              </>
            ) : (
              <button
                onClick={() => navigate('/login')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-light)',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: '500',
                  padding: '8px 0',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = 'var(--accent-primary)';
                  const underline = e.currentTarget.querySelector('.underline');
                  if (underline) underline.style.width = '100%';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = 'var(--text-light)';
                  const underline = e.currentTarget.querySelector('.underline');
                  if (underline) underline.style.width = '0';
                }}
              >
                Sign In
                <div className="underline" style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  height: '2px',
                  width: 0,
                  background: 'var(--accent-primary)',
                  transition: 'width 0.3s ease'
                }}></div>
              </button>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            className={`hamburger-btn ${showMobileMenu ? 'open' : ''}`}
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            aria-label="Toggle menu"
          >
            <div className="hamburger-line"></div>
            <div className="hamburger-line"></div>
            <div className="hamburger-line"></div>
          </button>
        </div>

        {/* Bottom row: Find / News / Opportunities */}
        <div className="nav-bottom-row">
          {/* Find */}
          <button
            onClick={() => {
              navigate('/find');
              setShowMobileMenu(false);
            }}
            className="nav-link"
            style={{
              background: 'none',
              border: 'none',
              color: location.pathname === '/find' ? 'var(--accent-primary)' : 'var(--text-light)',
              cursor: 'pointer',
              fontSize: '17px',
              fontWeight: '600',
              padding: '0 0 6px 0',
              letterSpacing: '1px',
              position: 'absolute',
              left: 'calc(50% - 240px)',
              transition: 'color 0.3s ease',
              borderBottom: location.pathname === '/find' ? '3px solid var(--accent-primary)' : 'none'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = 'var(--accent-primary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = location.pathname === '/find' ? 'var(--accent-primary)' : 'var(--text-light)';
            }}
          >
            Find
          </button>

          {/* News */}
          <button
            onClick={() => {
              navigate('/news');
              setShowMobileMenu(false);
            }}
            className="nav-link"
            style={{
              background: 'none',
              border: 'none',
              color: (location.pathname === '/news' || location.pathname === '/') ? 'var(--accent-primary)' : 'var(--text-light)',
              cursor: 'pointer',
              fontSize: '17px',
              fontWeight: '600',
              padding: '0 0 6px 0',
              letterSpacing: '1px',
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              transition: 'color 0.3s ease',
              borderBottom: location.pathname === '/news' ? '3px solid var(--accent-primary)' : 'none'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = 'var(--accent-primary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = (location.pathname === '/news' || location.pathname === '/') ? 'var(--accent-primary)' : 'var(--text-light)';
            }}
          >
            News
          </button>

          {/* Opportunities */}
          <button
            onClick={() => {
              navigate('/opportunities');
              setShowMobileMenu(false);
            }}
            className="nav-link"
            style={{
              background: 'none',
              border: 'none',
              color: location.pathname === '/opportunities' ? 'var(--accent-primary)' : 'var(--text-light)',
              cursor: 'pointer',
              fontSize: '17px',
              fontWeight: '600',
              padding: '0 0 6px 0',
              letterSpacing: '1px',
              position: 'absolute',
              left: 'calc(50% + 240px)',
              transform: 'translateX(-100%)',
              transition: 'color 0.3s ease',
              borderBottom: location.pathname === '/opportunities' ? '3px solid var(--accent-primary)' : 'none'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = 'var(--accent-primary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = location.pathname === '/opportunities' ? 'var(--accent-primary)' : 'var(--text-light)';
            }}
          >
            Opportunities
          </button>
        </div>
      </div>

      {/* Mobile menu overlay */}
      <div className={`mobile-menu-overlay ${showMobileMenu ? 'open' : ''}`}>
        {isAuthenticated ? (
          <>
            <button
              className="mobile-menu-item"
              onClick={() => {
                navigate('/profile');
                setShowMobileMenu(false);
              }}
            >
              My Account
            </button>
            <button
              className="mobile-menu-item"
              onClick={() => {
                navigate('/collaborations/requests');
                setShowMobileMenu(false);
              }}
            >
              Collab Requests
            </button>
            <button
              className="mobile-menu-item"
              onClick={() => {
                handleLogout();
                setShowMobileMenu(false);
              }}
            >
              Sign Out
            </button>
          </>
        ) : (
          <>
            <button
              className="mobile-menu-item"
              onClick={() => {
                navigate('/login');
                setShowMobileMenu(false);
              }}
            >
              Sign In
            </button>
            <button
              className="mobile-menu-item"
              onClick={() => {
                navigate('/register');
                setShowMobileMenu(false);
              }}
            >
              Register
            </button>
          </>
        )}
      </div>
    </>
  );
};

export default Navbar;
